import { useEffect, useId } from 'react';
import { X } from 'lucide-react';
import Portal from './Portal';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * Standard modal primitive. Enforces the sticky header / scrollable content /
 * optional sticky footer contract so every modal in the app behaves consistently.
 *
 * Uses the CSS classes defined in index.css (.modal-overlay, .modal-container,
 * .modal-header, .modal-content, .modal-footer).
 *
 * Props:
 *   isOpen           - boolean, controls visibility
 *   onClose          - callback when user clicks backdrop, close button, or presses Escape
 *   title            - string, rendered in the sticky header (ignored when customHeader is set)
 *   size             - 'sm' | 'md' | 'lg' | 'xl' (default 'lg')
 *   footer           - ReactNode, rendered in a sticky footer (omit for info-only modals)
 *   theme            - 'admin' | 'employee' (default 'admin')
 *   closable         - boolean (default true), shows close button and enables backdrop/Escape dismiss
 *   customHeader     - ReactNode, replaces the default header (still gets sticky positioning)
 *   className        - extra classes on .modal-container
 *   contentClassName - extra classes on .modal-content
 *   children         - scrollable body content
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'lg',
  footer,
  theme = 'admin',
  closable = true,
  customHeader,
  className = '',
  contentClassName = '',
  children,
}) => {
  const titleId = useId();

  // iOS-proof body scroll lock (pins <body> position:fixed; plain overflow:hidden
  // is ignored by iOS Safari for touch panning, letting the page rubber-band and
  // drag the fixed overlay around). Ref-counted, so nested modals are safe.
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e) => {
      if (e.key === 'Escape' && closable) onClose?.();
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, closable]);

  if (!isOpen) return null;

  const sizeClass = {
    sm: 'modal-container-sm',
    md: 'modal-container-md',
    lg: 'modal-container-lg',
    xl: 'modal-container-xl',
  }[size] || 'modal-container-lg';

  const closeClass =
    theme === 'admin' ? 'modal-close-admin' : 'modal-close';

  return (
    <Portal>
      <div
        className="modal-overlay"
        onClick={closable ? onClose : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div
          className={`modal-container ${sizeClass} ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {customHeader ? (
            <div className="flex-shrink-0 sticky top-0 z-[10001]">
              {customHeader}
            </div>
          ) : (
            <div className="modal-header">
              <h3 id={titleId} className="modal-title">{title}</h3>
              {closable && (
                <button
                  type="button"
                  onClick={onClose}
                  className={closeClass}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          <div className={`modal-content ${contentClassName}`}>
            {children}
          </div>

          {footer && (
            <div className="modal-footer">
              {footer}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default Modal;
